import React, { useEffect, useState } from 'react';
import { StreamingIndicator } from './Indicators';
import { TokenUsage } from './TokenUsageDisplay';

import { useTranslation } from 'react-i18next';

interface LoadingStateProps {
  isLoading: boolean;
  hasMessages: boolean;
  lastMessageIsUser: boolean;
  startTime?: number;
  tokenUsage?: TokenUsage;
}

export const LoadingState: React.FC<LoadingStateProps> = React.memo(
  ({
    isLoading,
    hasMessages,
    lastMessageIsUser,
    startTime,
    tokenUsage,
  }: LoadingStateProps) => {
    const { t } = useTranslation('pleri/chat');
    const [elapsedTime, setElapsedTime] = useState(0);

    useEffect(() => {
      if (!isLoading || !startTime) {
        setElapsedTime(0);
        return;
      }

      // Calculate initial elapsed time
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));

      // Update elapsed time every second
      const interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);

      return () => clearInterval(interval);
    }, [isLoading, startTime]);

    if (!isLoading) {
      return null;
    }

    const baseText = t('writingResponse');

    const timeText = elapsedTime > 0 ? `${elapsedTime}s` : '';
    const tokenText = tokenUsage?.totalTokens
      ? `↓ ${tokenUsage.totalTokens.toLocaleString()} tokens`
      : '';

    const additionalInfo = [timeText, tokenText].filter(Boolean).join(' · ');

    return (
      <div className="mb-6 flex items-center justify-between">
        <StreamingIndicator text={`${baseText}...`} />
        {additionalInfo && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            ({additionalInfo})
          </div>
        )}
      </div>
    );
  },
);

LoadingState.displayName = 'LoadingState';
